import type {
  FluentVariant,
  GenericPalette,
  MaterialAccent,
  ThemeFamily,
  ThemeMode,
  ThemePreferences
} from './themeCatalog';
import {
  DEFAULT_THEME_PREFERENCES,
  FLUENT_VARIANTS,
  GENERIC_PALETTES,
  MATERIAL_ACCENTS
} from './themeCatalog';

export function resolveThemeId(prefs: ThemePreferences): string {
  if (prefs.family === 'material') {
    return `material.${prefs.materialAccent}.${prefs.mode}`;
  }
  if (prefs.family === 'fluent') {
    if (prefs.fluentVariant === 'red-storm') {
      return 'fluent.red-storm.dark';
    }
    const suffix = prefs.fluentCompact ? '.compact' : '';
    return `fluent.${prefs.fluentVariant}.${prefs.mode}${suffix}`;
  }
  const suffix = prefs.genericCompact ? '.compact' : '';
  return `generic.${prefs.genericPalette}${suffix}`;
}

export function canToggleMode(prefs: ThemePreferences): boolean {
  if (prefs.family === 'fluent' && prefs.fluentVariant === 'red-storm') return false;
  if (prefs.family === 'material' || prefs.family === 'fluent') return true;
  return prefs.genericPalette === 'light' || prefs.genericPalette === 'dark';
}

/** Maps active DevExtreme theme to html `[data-theme]` for platform tokens.css */
export function resolveHtmlDataTheme(prefs: ThemePreferences, themeId: string): ThemeMode {
  if (prefs.family === 'material' || prefs.family === 'fluent') return prefs.mode;
  if (themeId.includes('.dark')) return 'dark';
  const darkPalettes: GenericPalette[] = ['dark', 'darkmoon', 'darkviolet', 'contrast'];
  if (darkPalettes.includes(prefs.genericPalette)) return 'dark';
  return 'light';
}

export function preferencesEqual(a: ThemePreferences, b: ThemePreferences): boolean {
  return (
    a.family === b.family &&
    a.materialAccent === b.materialAccent &&
    a.genericPalette === b.genericPalette &&
    a.genericCompact === b.genericCompact &&
    a.fluentVariant === b.fluentVariant &&
    a.fluentCompact === b.fluentCompact &&
    a.mode === b.mode
  );
}

function isMaterialAccent(v: unknown): v is MaterialAccent {
  return typeof v === 'string' && (MATERIAL_ACCENTS as readonly string[]).includes(v);
}

function isFluentVariant(v: unknown): v is FluentVariant {
  return typeof v === 'string' && (FLUENT_VARIANTS as readonly string[]).includes(v);
}

function isGenericPalette(v: unknown): v is GenericPalette {
  return typeof v === 'string' && (GENERIC_PALETTES as readonly string[]).includes(v);
}

function isThemeFamily(v: unknown): v is ThemeFamily {
  return v === 'material' || v === 'generic' || v === 'fluent';
}

function isThemeMode(v: unknown): v is ThemeMode {
  return v === 'light' || v === 'dark';
}

/** Normalize persisted Zustand state (including legacy `{ mode }` only). */
export function migrateThemePreferences(raw: unknown): ThemePreferences {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_THEME_PREFERENCES };
  }
  const o = raw as Record<string, unknown>;
  const mode = isThemeMode(o.mode)
    ? o.mode
    : systemPrefersDark()
      ? 'dark'
      : DEFAULT_THEME_PREFERENCES.mode;

  if (!isThemeFamily(o.family)) {
    return {
      ...DEFAULT_THEME_PREFERENCES,
      mode
    };
  }

  return {
    family: o.family,
    materialAccent: isMaterialAccent(o.materialAccent)
      ? o.materialAccent
      : DEFAULT_THEME_PREFERENCES.materialAccent,
    genericPalette: isGenericPalette(o.genericPalette)
      ? o.genericPalette
      : DEFAULT_THEME_PREFERENCES.genericPalette,
    genericCompact: Boolean(o.genericCompact),
    fluentVariant: isFluentVariant(o.fluentVariant)
      ? o.fluentVariant
      : DEFAULT_THEME_PREFERENCES.fluentVariant,
    fluentCompact: Boolean(o.fluentCompact),
    mode
  };
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** After toolbar toggle: update mode and Generic light/dark palette pair. */
export function nextPreferencesAfterModeToggle(prefs: ThemePreferences): ThemePreferences {
  if (prefs.family === 'fluent' && prefs.fluentVariant === 'red-storm') {
    return { ...prefs, mode: 'dark' };
  }
  const nextMode: ThemeMode = prefs.mode === 'dark' ? 'light' : 'dark';
  if (prefs.family === 'generic' && (prefs.genericPalette === 'light' || prefs.genericPalette === 'dark')) {
    return {
      ...prefs,
      mode: nextMode,
      genericPalette: nextMode
    };
  }
  return { ...prefs, mode: nextMode };
}
